"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.formatCents = formatCents;
/**
 * Formats a value in cents as a string.
 * @example
 * formatCents(123456) // $1,234.56
 * formatCents(-500) // -$5.00
 * formatCents(null) // $-.--
 *
 * // Parentheses for negative values:
 * formatCents(-500, "(#.##)") // (5.00)
 * 
 * // Custom number of decimal places:
 * formatCents(314.159, "-$#.##", 4) // $3.1416
 * 
 * @param cents The number of cents to format.
 * @param formatString The format string to use. Can be one of the following:
 * - `-$#.##`: (Default) Formats positive numbers as `$#.##` and negative numbers as `-$#.##`. Examples: `-$5.00`, `$1,234.56`, `$-.--`
 * - `(#.##)`: Formats positive numbers as `#.##` and negative numbers as `(#.##)`. Examples: `(5.00)`, `1,234.56`, `-.--`
 * @param fractionDigits The number of digits to include after the decimal point. Must be between 0 and 16, inclusive. Defaults to 2.
 * @returns The formatted price string.
 * @notes The number of cents is rounded according to the chosen value of fractionDigits before formatting. This means
 * that negative numbers which round to 0 (such as -0.2) will be formatted as $0.00, without the negative sign.
 */
function formatCents(cents, formatString = "-$#.##", fractionDigits = 2) {
  if (!(fractionDigits >= 0 && fractionDigits <= 16)) {
    throw new TypeError("Invalid value for fractionDigits. Value must be between 0 and 16, inclusive. Received: " + fractionDigits);
  }
  const isNumber = typeof cents === 'number' && Number.isFinite(cents);
  const pow10 = Math.pow(10, fractionDigits - 2);
  const roundedCents = isNumber ? Math.round(cents * pow10) / pow10 : 0;
  const dollarString = isNumber ? (Math.abs(cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }) : fractionDigits > 0 ? "-." + "-".repeat(fractionDigits) : "-";
  const useParens = formatString.includes("(");
  const isNegative = roundedCents < 0;
  const includeDollarSign = formatString.includes("$");
  if (useParens) {
    if (isNegative) {
      return `(${dollarString})`;
    } else {
      return dollarString;
    }
  } else if (includeDollarSign) {
    if (isNegative) {
      return `-$${dollarString}`;
    } else {
      return `$${dollarString}`;
    }
  } else {
    if (isNegative) {
      return `-${dollarString}`;
    } else {
      return dollarString;
    }
  }
}